import { FC, useContext, FormEvent, SetStateAction, Dispatch } from "react";
import {
  ShareLinkFormModalProvider,
  ShareLinkFormModalContext,
} from "./ShareLinkFormModalContext";
import {
  useCreateShareLinkMutation,
  useUpdateShareLinkMutation,
  ShareLinkTypeEnum,
} from "../../../generated/graphql";
import {
  Dialog,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
} from "@material-ui/core";
import { ButtonCustom, CancelButton } from "../../shared/Style/Style";
import { ShareLinkForm } from "../styles";
import { CloseIconButton } from "../../shared/Buttons/CloseIconButton/CloseIconButton";
import { DialogHeader } from "../../MobileAppModal/MobileAppModal.style";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress";
import { SelectArbookContent } from "../components/SelectArbookContent";
import { ShareLinkQr } from "../components/ShareLinkQr";
import { validateShareLink, toDateInputValue } from "../utils";
import { initialNotifications } from "../ShareLinks";

interface ShareLinkFormModalProps {
  id?: string;
  onClose: () => void;
  setNotifications: Dispatch<
    SetStateAction<{ error: boolean; success: boolean; message: string }>
  >;
}

const TYPE_OPTIONS = [
  { value: ShareLinkTypeEnum.Content, label: "Контент ArBook+" },
  { value: ShareLinkTypeEnum.ExternalUrl, label: "Зовнішнє посилання" },
];

const ShareLinkFormModalContent: FC<ShareLinkFormModalProps> = ({
  id,
  onClose,
  setNotifications,
}) => {
  const { formData, updateFormData } = useContext(ShareLinkFormModalContext);

  const [createShareLink, { loading: createLoading }] =
    useCreateShareLinkMutation();
  const [updateShareLink, { loading: updateLoading }] =
    useUpdateShareLinkMutation();

  const isEdit = !!id;
  if (isEdit && !formData.id) return null;

  const type = formData.type || ShareLinkTypeEnum.Content;
  const isExternal = type === ShareLinkTypeEnum.ExternalUrl;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const expiresAt = formData.expiresAt || null;
    const targetUlcId = isExternal ? null : formData.targetUlcId || null;
    const externalUrl = isExternal ? formData.externalUrl?.trim() || null : null;
    // CONTENT: чекбокс (не торкались — true за замовчуванням); EXTERNAL_URL: null (бекенд ігнорує)
    const forceContentAccess = isExternal
      ? null
      : formData.forceContentAccess ?? true;

    try {
      if (isEdit) {
        await updateShareLink({
          variables: {
            input: {
              id: formData.id as string,
              type,
              targetUlcId,
              externalUrl,
              forceContentAccess,
              name: formData.name?.trim() || null,
              description: formData.description?.trim() || null,
              isActive: formData.isActive,
              expiresAt,
            },
          },
          refetchQueries: ["shareLinks"],
          awaitRefetchQueries: true,
          onCompleted: (data) => {
            if (data?.updateShareLink?.id) {
              setNotifications({
                ...initialNotifications,
                success: true,
                message: "Посилання успішно оновлено",
              });
            }
          },
          onError: (error) => {
            setNotifications({
              ...initialNotifications,
              error: true,
              message: "Помилка при оновленні посилання",
            });
            console.error(error);
          },
        });
      } else {
        await createShareLink({
          variables: {
            input: {
              type,
              targetUlcId,
              externalUrl,
              forceContentAccess,
              name: formData.name?.trim() || null,
              description: formData.description?.trim() || null,
              expiresAt,
            },
          },
          refetchQueries: ["shareLinks"],
          awaitRefetchQueries: true,
          onCompleted: (data) => {
            if (data?.createShareLink?.id) {
              setNotifications({
                ...initialNotifications,
                success: true,
                message: "Посилання успішно створено",
              });
            }
          },
          onError: (error) => {
            setNotifications({
              ...initialNotifications,
              error: true,
              message: "Помилка при створенні посилання",
            });
            console.error(error);
          },
        });
      }
    } catch {
      setNotifications({
        ...initialNotifications,
        error: true,
        message: "Сталась помилка, спробуйте пізніше",
      });
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { padding: "32px" } }}
    >
      {(createLoading || updateLoading) && <LoadingProgressOverlay />}
      <DialogHeader>
        <Typography variant="h6">
          {isEdit ? "Редагування посилання" : "Створення посилання"}
        </Typography>
        <CloseIconButton close={onClose} />
      </DialogHeader>

      <ShareLinkForm onSubmit={handleSubmit}>
        {isEdit && formData.shareUrl && (
          <ShareLinkQr
            shareUrl={formData.shareUrl}
            shortCode={formData.shortCode}
          />
        )}

        <TextField
          select
          label="Тип посилання"
          value={type}
          variant="outlined"
          fullWidth
          onChange={(e) =>
            updateFormData("type", e.target.value as ShareLinkTypeEnum)
          }
        >
          {TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {isExternal ? (
          <TextField
            label="Зовнішнє посилання (URL) *"
            value={formData.externalUrl || ""}
            placeholder="https://..."
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 2048 }}
            onChange={(e) => updateFormData("externalUrl", e.target.value)}
          />
        ) : (
          <SelectArbookContent
            value={
              formData.targetUlcId
                ? { id: formData.targetUlcId, name: formData.targetUlc?.name }
                : null
            }
            onChange={(option) =>
              updateFormData({
                targetUlcId: option?.id,
                targetUlc: option ? ({ ...option } as any) : undefined,
              })
            }
          />
        )}

        {!isExternal && (
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.forceContentAccess ?? true}
                onChange={(e) =>
                  updateFormData("forceContentAccess", e.target.checked)
                }
                color="primary"
              />
            }
            label="Відкривати контент незалежно від умов покупки"
          />
        )}

        <TextField
          label="Назва"
          value={formData.name || ""}
          variant="outlined"
          fullWidth
          inputProps={{ maxLength: 200 }}
          onChange={(e) => updateFormData("name", e.target.value)}
        />

        <TextField
          label="Опис"
          value={formData.description || ""}
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          inputProps={{ maxLength: 500 }}
          onChange={(e) => updateFormData("description", e.target.value)}
        />

        <TextField
          label="Дата закінчення"
          type="date"
          value={toDateInputValue(formData.expiresAt)}
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          onChange={(e) => updateFormData("expiresAt", e.target.value || null)}
        />

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.isActive}
                onChange={(e) => updateFormData("isActive", e.target.checked)}
                color="primary"
              />
            }
            label="Активне"
          />
        )}

        <div style={{ display: "flex", justifyContent: "space-evenly" }}>
          <CancelButton onClick={onClose} type="button">
            Закрити
          </CancelButton>
          <ButtonCustom disabled={!validateShareLink(formData)} type="submit">
            Зберегти
          </ButtonCustom>
        </div>
      </ShareLinkForm>
    </Dialog>
  );
};

export const ShareLinkFormModal: FC<ShareLinkFormModalProps> = ({
  id,
  onClose,
  setNotifications,
}) => (
  <ShareLinkFormModalProvider id={id}>
    <ShareLinkFormModalContent
      id={id}
      onClose={onClose}
      setNotifications={setNotifications}
    />
  </ShareLinkFormModalProvider>
);
